import {
	buildWorkDetailRows,
	WorkDetailRow,
	WorkDetailTimeReg,
} from '../../../components/insights/invoicing/line-generation/invoicing_work_detail_rows';

/**
 * Work detail for the "one invoice per project" bulk flow.
 *
 * N projects produce N invoices, so each invoice's rows are just that project's own work — unlike the
 * single-invoice flow, where several projects collapse into one row list.
 *
 * The toggle, grouping and date window are chosen once for the whole batch and applied to every
 * project, which is why they arrive here as scalars rather than per project.
 *
 * As everywhere else in work detail: a row carries no time-registration reference, so nothing built
 * here can mark time as invoiced.
 */

/** A generated bulk invoice line. Only the project it bills matters here. */
export type BulkWorkDetailLine = {
	projectId?: string | null;
};

type BulkWorkDetailParams = {
	lines: readonly BulkWorkDetailLine[];
	/** Every time registration for a project, keyed by project id. NOT the uninvoiced pool. */
	timeRegsByProject: Record<string, WorkDetailTimeReg[]>;
	grouping: string;
	showRates: boolean;
	quantityDecimals: number;
	startDate?: string;
	endDate?: string;
	/**
	 * Converts a registration's price into the batch's invoice currency. Forwarded unchanged to every
	 * project: the whole batch shares one invoice currency, and the converter already resolves each
	 * registration's source currency from its own project's rate card.
	 */
	priceToInvoiceCurrency?: (price: number, timeReg: WorkDetailTimeReg) => number;
};

/**
 * One row list per project id, ready to hand to that project's entry in the bulk mutation payload.
 * A project with no rows is still present with an empty list, so the caller never has to distinguish
 * "no work detail" from "project not considered".
 */
export const buildBulkWorkDetailRowsByProject = ({
	lines,
	timeRegsByProject,
	grouping,
	showRates,
	quantityDecimals,
	startDate,
	endDate,
	priceToInvoiceCurrency,
}: BulkWorkDetailParams): Record<string, WorkDetailRow[]> => {
	const rowsByProject: Record<string, WorkDetailRow[]> = {};

	const projectIds = new Set(lines.map(line => line.projectId).filter((id): id is string => !!id));

	projectIds.forEach(projectId => {
		rowsByProject[projectId] = buildWorkDetailRows(timeRegsByProject[projectId] ?? [], {
			grouping,
			showRates,
			quantityDecimals,
			startDate,
			endDate,
			priceToInvoiceCurrency,
		});
	});

	return rowsByProject;
};

/** Every time registration of every project in the bulk query, keyed by project id. */
export const bulkTimeRegsByProject = (
	projectEdges: readonly ({
		node?: {id?: string | null; timeRegistrations?: {edges?: readonly ({node?: unknown} | null)[] | null} | null} | null;
	} | null)[]
): Record<string, WorkDetailTimeReg[]> => {
	const byProject: Record<string, WorkDetailTimeReg[]> = {};
	(projectEdges ?? []).forEach(edge => {
		const project = edge?.node;
		if (!project || !project.id) return;
		byProject[project.id] = (project.timeRegistrations?.edges ?? [])
			.map(timeRegEdge => timeRegEdge?.node)
			.filter((node): node is WorkDetailTimeReg => !!node);
	});
	return byProject;
};
